import { z } from "zod";
import {
  centsSchema,
  currencySchema,
  reasonSchema,
  shortTextSchema,
  ulidSchema,
} from "./primitives.js";

export const PRIZE_BASES = ["participation", "placement", "pooled", "agreed"] as const;
export const PRIZE_ELIGIBILITIES = ["all", "placed", "unplaced", "outside_top_n"] as const;
/**
 * How often a rule pays.
 *
 * `per_day` is not sugar for `per_session`. This pilot runs three sessions on the Saturday — a
 * morning of horseshoes, the judged afternoon, the evening Grand Entry — and a poster that says
 * HONORARIUM PAID DAILY means one payment that Saturday, not three. Expressing it with sessions
 * meant either paying per block or picking one session a day and hoping nobody checked in at
 * another (D74).
 */
export const PRIZE_CADENCES = ["per_session", "per_day", "per_event"] as const;

export const prizeRulePlacementSchema = z.object({
  place: z.number().int().min(1).max(50),
  amountCents: centsSchema,
});

/**
 * One target a prize rule applies to (D52). The ids compose — a competition alone, a value alone
 * (that band anywhere), the two together (that band within that competition), or a category. A rule
 * with no targets applies to the whole event, so an empty set is the "everyone" case.
 *
 * `eventRoleId` names a role instead (D63) — the elders, the invited drums, the arena director. It
 * does not compose with the other three, because a role holder has no competition and no category
 * for them to narrow.
 */
export const prizeScopeSchema = z
  .object({
    competitionId: ulidSchema.nullable().optional(),
    categoryId: ulidSchema.nullable().optional(),
    valueId: ulidSchema.nullable().optional(),
    eventRoleId: ulidSchema.nullable().optional(),
  })
  .refine(
    (scope) =>
      scope.eventRoleId == null ||
      (scope.competitionId == null && scope.categoryId == null && scope.valueId == null),
    {
      message: "A role target stands on its own — it has no competition or category to narrow",
      path: ["eventRoleId"],
    },
  );

/**
 * A prize rule (D16).
 *
 * The four axes are independent, and the refinements below are the only coupling between them —
 * each says "this basis needs this amount field", never "this basis implies that eligibility".
 * That separation is what lets a community express a flat honorarium to everyone who danced, the
 * same amount to non-winners only, a top-up for winners, or a pool split among an exhibition,
 * without any of them being a special case.
 */
export const createPrizeRuleSchema = z
  .object({
    name: shortTextSchema,
    /** The targets this rule applies to (D52). Empty or absent is the whole event. Naming a band and
     *  a competition together — "Juniors under Dance" — is how the hierarchy is expressed. */
    scopes: z.array(prizeScopeSchema).optional(),
    basis: z.enum(PRIZE_BASES),
    eligibility: z.enum(PRIZE_ELIGIBILITIES).default("all"),
    eligibilityRankCutoff: z.number().int().min(1).nullable().optional(),
    /** The three money fields belong to different bases, and a form that states all of them sends
     *  null for the two the chosen basis does not use. Null is "this basis has no such amount",
     *  which the refinements below read exactly as they read an absent field. */
    amountCents: centsSchema.nullable().optional(),
    poolTotalCents: centsSchema.nullable().optional(),
    poolSplit: z.enum(["equal", "by_placement"]).nullable().optional(),
    placements: z.array(prizeRulePlacementSchema).optional(),
    cadence: z.enum(PRIZE_CADENCES).default("per_event"),
    currency: currencySchema.default("CAD"),
    /** Registering and not dancing earns nothing. Defaults on, deliberately. */
    requiresCheckin: z.boolean().default(true),
    active: z.boolean().default(true),
  })
  .refine((rule) => rule.basis !== "participation" || rule.amountCents != null, {
    message: "A per-participant rule needs an amount",
    path: ["amountCents"],
  })
  .refine((rule) => rule.basis !== "placement" || (rule.placements?.length ?? 0) > 0, {
    message: "A placement rule needs at least one place and amount",
    path: ["placements"],
  })
  .refine((rule) => rule.basis !== "pooled" || rule.poolTotalCents != null, {
    message: "A pooled rule needs a total to divide",
    path: ["poolTotalCents"],
  })
  .refine((rule) => rule.eligibility !== "outside_top_n" || rule.eligibilityRankCutoff != null, {
    message: "Say which rank the cutoff sits below",
    path: ["eligibilityRankCutoff"],
  })
  /**
   * A rule targeting a role must pay everyone holding it.
   *
   * Roles have no results, and the calculator's `unplaced` branch qualifies anyone without one —
   * which is right for a dancer whose category was not tabulated and quietly wrong for someone who
   * was never in a category. Refused here rather than handled there, because the honest fix is that
   * the question does not apply (D63).
   */
  .refine(
    (rule) =>
      rule.eligibility === "all" || !(rule.scopes ?? []).some((scope) => scope.eventRoleId != null),
    {
      message: "A rule that pays a role pays everyone holding it — a role has no placing",
      path: ["eligibility"],
    },
  )
  /** `agreed` takes its amount from the assignment, so a rule-level amount would be ignored — and
   *  an ignored number on a money screen is worse than a rejected one. */
  .refine((rule) => rule.basis !== "agreed" || rule.amountCents == null, {
    message: "An agreed fee takes its amount from the person, not from the rule",
    path: ["amountCents"],
  })
  .refine(
    (rule) =>
      rule.basis !== "agreed" || (rule.scopes ?? []).every((scope) => scope.eventRoleId != null),
    {
      message: "An agreed fee is settled with a role holder, so it can only target a role",
      path: ["scopes"],
    },
  )
  .refine(
    (rule) =>
      rule.basis !== "placement" ||
      new Set(rule.placements?.map((p) => p.place)).size === (rule.placements?.length ?? 0),
    { message: "Each place can appear only once", path: ["placements"] },
  );

export const updatePrizeRuleSchema = z
  .object({
    name: shortTextSchema.optional(),
    /** A full restatement of the targets (D52); an empty array moves the rule to the whole event. */
    scopes: z.array(prizeScopeSchema).optional(),
    /** What the rule pays on. Editable like every other part of a restatement — it was omitted here
     *  once, and an omitted key is *stripped* rather than refused, so the rule editor sent a new
     *  basis on every save and none of them ever landed. */
    basis: z.enum(PRIZE_BASES).optional(),
    /** Null clears the amount, which is what changing a rule's basis has to do to the fields the old
     *  basis used; an absent field leaves it alone. */
    amountCents: centsSchema.nullable().optional(),
    poolTotalCents: centsSchema.nullable().optional(),
    poolSplit: z.enum(["equal", "by_placement"]).nullable().optional(),
    placements: z.array(prizeRulePlacementSchema).optional(),
    eligibility: z.enum(PRIZE_ELIGIBILITIES).optional(),
    eligibilityRankCutoff: z.number().int().min(1).nullable().optional(),
    cadence: z.enum(PRIZE_CADENCES).optional(),
    requiresCheckin: z.boolean().optional(),
    active: z.boolean().optional(),
  })
  /**
   * The same rule as on create, applied to what this restatement actually says.
   *
   * Only checkable when the edit sends both halves, which is what the rule editor does — it restates
   * targets and eligibility together. An edit that sends one half alone is checked against the
   * stored rule by the repository, where the other half can be read (D63).
   */
  .refine(
    (rule) =>
      rule.scopes === undefined ||
      rule.eligibility === undefined ||
      rule.eligibility === "all" ||
      !rule.scopes.some((scope) => scope.eventRoleId != null),
    {
      message: "A rule that pays a role pays everyone holding it — a role has no placing",
      path: ["eligibility"],
    },
  )
  /**
   * A restatement that names a basis carries that basis's money in the same request.
   *
   * Only checkable when the basis is being sent, which is exactly when it matters: changing what a
   * rule pays on changes which amount field means anything, and accepting the new basis without the
   * new amount would leave a placement rule holding a per-person figure. A number the calculator
   * ignores, sitting on a money screen, is worse than a rejection.
   */
  .refine((rule) => rule.basis !== "participation" || rule.amountCents != null, {
    message: "A per-participant rule needs an amount",
    path: ["amountCents"],
  })
  .refine((rule) => rule.basis !== "placement" || (rule.placements?.length ?? 0) > 0, {
    message: "A placement rule needs at least one place and amount",
    path: ["placements"],
  })
  .refine((rule) => rule.basis !== "pooled" || rule.poolTotalCents != null, {
    message: "A pooled rule needs a total to divide",
    path: ["poolTotalCents"],
  })
  .refine((rule) => rule.basis !== "agreed" || rule.amountCents == null, {
    message: "An agreed fee takes its amount from the person, not from the rule",
    path: ["amountCents"],
  });

export const calculatePayoutsSchema = z.object({
  sessionId: ulidSchema.optional(),
  /** Recalculating supersedes rather than mutates: new `calculated` rows, old ones voided. */
  supersedeExisting: z.boolean().default(true),
});

export const approvePayoutsSchema = z.object({
  payoutIds: z.array(ulidSchema).min(1),
});

export const markPaidSchema = z.object({
  payoutIds: z.array(ulidSchema).min(1),
  paidMethod: z.enum(["cash", "cheque", "transfer", "other"]).default("cash"),
  reference: shortTextSchema.optional(),
});

/** A paid line is immutable. A correction is a void with a reason plus a new line (ux.md §3.9). */
export const voidPayoutSchema = z.object({
  reason: reasonSchema,
});

/**
 * Taking a payment back.
 *
 * A reason is not optional here for the same reason it is not optional on a void: this is a record
 * of money, and "it changed and nobody said why" is the state the whole ledger exists to prevent.
 */
export const unpayPayoutSchema = z.object({
  reason: reasonSchema,
});

/**
 * Setting an amount by hand.
 *
 * Non-negative rather than positive, because zero is a real decision — a line withdrawn to nothing
 * without erasing the record that it was owed. The upper bound is a typo guard, not a policy: it is
 * a million dollars in cents, and it exists so a slipped keypress produces a rejection rather than
 * a payout sheet nobody notices is wrong.
 */
export const overridePayoutSchema = z.object({
  amountCents: z.number().int().min(0).max(100_000_000),
  reason: reasonSchema,
});

export type CreatePrizeRuleInput = z.infer<typeof createPrizeRuleSchema>;
export type PrizeBasis = (typeof PRIZE_BASES)[number];
export type PrizeEligibility = (typeof PRIZE_ELIGIBILITIES)[number];
export type PrizeCadence = (typeof PRIZE_CADENCES)[number];
